import { z } from 'zod';
import {
  DayHours,
  EmailOptInSource,
  ErrorBody,
  IsoDate,
  IsoDateTime,
  SmsOptInSource,
} from '@yourstore/domain';

/**
 * The staff wire, as this client understands it.
 *
 * It mirrors `apps/core-api/src/wire.ts` by hand and on purpose. Apps do not
 * cross-import (dev.md §3: the dependency arrows run through `packages/`, and
 * `core-api`'s wire module imports `@yourstore/store`, which would drag Drizzle
 * and the `postgres` driver into a browser bundle). What the two files *do*
 * share is `@yourstore/domain`, so the scalars that carry a promise —
 * `IsoDateTime` with an explicit offset, `IsoDate`, `DayHours` — are one
 * definition rather than two lookalikes.
 *
 * Everything the network returns is parsed through these schemas before it
 * reaches the mirror. That is the point of them: the mirror is what the app
 * shows while offline, and a malformed row written into it is a lie the device
 * repeats for as long as the tunnel lasts.
 */

export const SessionRoute = z.object({
  id: z.string(),
  method: z.string(),
  path: z.string(),
  module: z.string().nullable(),
  write: z.boolean(),
});

/**
 * What settles the remainder (spec §6.5) — the six **recorded** external
 * tenders, and the only choice the till makes (B4).
 *
 * Recorded, never processed: tapping "Venmo" writes down that the guest sent a
 * Venmo, it does not ask Venmo for anything, and `paypal` is emphatically not
 * the platform's PayPal payment link with its merchant fee. The owner of
 * `docs/lovespa-discovery.md` §4 dropped cards on purpose and takes cash, check,
 * Venmo and PayPal; what she needs written down is which.
 *
 * Which of the six this tenant's till *shows* is `Session.till.tenders`. This
 * enum is the vocabulary; that list is the drawer.
 *
 * It is declared **here**, three hundred lines above the rest of the money
 * wire it belongs to, for one unglamorous reason: `Session` below reads it, zod
 * schemas are values built at module evaluation, and a `const` referenced before
 * its line has run is a blank screen at boot rather than a type error. One
 * definition in the wrong paragraph beats two in the right ones.
 */
export const RemainderKind = z.enum(['cash', 'check', 'venmo', 'paypal', 'card', 'other']);
export type RemainderKind = z.infer<typeof RemainderKind>;

export const Session = z.object({
  user: z.object({ uid: z.string(), role: z.enum(['owner', 'staff', 'readonly']) }),
  tenant: z.object({
    tenant_id: z.string(),
    name: z.string(),
    timezone: z.string(),
    currency: z.string(),
    status: z.enum(['onboarding', 'importing', 'live', 'paused', 'offboarded']),
  }),
  modules: z.array(z.string()),
  can_write: z.boolean(),
  /**
   * **May this seat change what the business sells?** (B10) — products,
   * services and their photographs, decided by the business's own
   * `catalog_editors` setting. Optional and tolerant: a session mirrored before
   * the field existed, or a `core-api` one deploy behind, reads through
   * `canChangeCatalog` in `ui/seat.tsx` as B7's rule — the owner, writing.
   */
  can_change_catalog: z.boolean().optional().catch(undefined),
  /** The business's answer — `owner` or `owner_and_staff` — for the owner's Settings. */
  catalog_editors: z.enum(['owner', 'owner_and_staff']).optional().catch(undefined),
  /**
   * **Whose eyes these are, and when they close** — present only when this
   * device is the operator console's fifteen-minute, read-only look
   * (`auth/support.ts`; `core-api`'s `routes/session.ts`). The strip draws
   * its banner from this rather than from a second read, so the banner is on
   * the same page as the book it qualifies. `.optional().catch(undefined)`
   * for `till`'s reason: absent is every real seat, and a malformed block is
   * a session without a banner rather than a session that will not parse.
   */
  support: z
    .object({ operator: z.string().min(1), expires_at: z.string().min(1) })
    .optional()
    .catch(undefined),
  routes: z.array(SessionRoute),
  /**
   * **The till's buttons** — which of the six recorded tenders this counter
   * shows, in the owner's own order (B4, `tenant_config.config.till.tenders`).
   *
   * Tolerant in both directions, and deliberately so. `.optional().catch()`
   * covers a `core-api` one deploy behind this bundle; the inner
   * `RemainderKind.catch('other')` would map a *seventh* kind this bundle has
   * never heard of onto a button it can draw. Between them, the worst outcome of
   * any version skew is today's three buttons, which is what `Checkout.tsx`
   * falls back to when the field is absent — never a till that will not paint.
   */
  till: z
    .object({
      tenders: z.array(RemainderKind.catch('other')).min(1).optional(),
      /**
       * **Whether this till asks for a tip** (0046) — `false` unless the owner
       * said otherwise, and the default is the decision. A solo owner working
       * alone tips nobody, and three percentage chips in front of every guest
       * she has known for nine years is the software making a suggestion on her
       * behalf that she never asked it to make. She is the first real tenant.
       */
      tips: z.boolean().optional(),
    })
    .optional()
    .catch(undefined),
  /**
   * **The house's grid step and its gap** (S1; A2's `buffersFor` policy half).
   *
   * The calendar draws blocks at their real span and a lighter tail after each
   * for the room the booking actually takes off the room — and it resolves that
   * tail through `buffersFor` in `@yourstore/scheduling`, which needs the
   * tenant's defaults beside the item's own two numbers. `slot_grid_min` is what
   * a keyboard arrow moves a block by and what a drag snaps to, so that a time
   * reached by dragging is a time the engine would have offered.
   *
   * `.optional().catch(undefined)` for `till`'s reason exactly: a `core-api` one
   * deploy behind this bundle answers without it, and the honest fallback is the
   * schema's own defaults (15 minutes, no house gap) rather than a calendar that
   * will not paint. `calendarPolicy` in `ui/calendar-grid.ts` is the one place
   * that fallback is spelled.
   */
  scheduling: z
    .object({
      slot_grid_min: z.number().int().min(5).max(120).catch(15),
      default_buffer_before_min: z.number().int().min(0).max(240).catch(0),
      default_buffer_after_min: z.number().int().min(0).max(240).catch(0),
    })
    .optional()
    .catch(undefined),
  /**
   * The staff PWA's named set (`docs/lovespa-personality.md`; `StaffBranding` in
   * `@yourstore/domain`, which `core-api` projects through `staffBrandingOf`).
   *
   * **Additive and defensively so**, the same shape `apps/site`'s `hours` and
   * `sells` take: it is absent from every session response this bundle met
   * before the personality wave, and it is absent from every tenant that has
   * published none — which is all of them but one. `.catch(undefined)` turns a
   * value this bundle cannot read into "no staff branding", which is *today's
   * look, byte for byte*, rather than into a counter iPad that will not paint.
   *
   * The enums are closed here on purpose, unlike `Booking.status` on the guest
   * side. There the argument is that a guest must not meet a blank page because
   * the server learned a sixth word; here a sixth `action_icon` is an icon this
   * bundle does not have a component for, and the honest answer to that is the
   * default one.
   */
  branding_staff: z
    .object({
      accent: z.string().optional().catch(undefined),
      ground: z.string().optional().catch(undefined),
      background_portrait_url: z.string().optional().catch(undefined),
      background_landscape_url: z.string().optional().catch(undefined),
      font_display: z.enum(['system', 'serif']).optional().catch(undefined),
      brandmark: z.enum(['plain', 'hearts']).optional().catch(undefined),
      action_icon: z.enum(['plus', 'heart']).optional().catch(undefined),
      buttons: z.enum(['solid', 'gradient', 'animated']).optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
});
export type Session = z.infer<typeof Session>;

/** The named set as this bundle holds it — every field optional, absent = today. */
export type StaffBranding = NonNullable<Session['branding_staff']>;

export const WireIdentity = z.object({
  identity_kind: z.enum(['phone', 'email']),
  identity: z.string(),
});
export type WireIdentity = z.infer<typeof WireIdentity>;

export const ClientSummary = z.object({
  client_id: z.string(),
  display_name: z.string(),
  archived: z.boolean(),
  is_walk_in_account: z.boolean(),
  tags: z.array(z.string()),
  identities: z.array(WireIdentity),
  visit_count: z.number(),
  last_visit_at: z.string().nullable(),
});
export type ClientSummary = z.infer<typeof ClientSummary>;

export const Preference = z.object({
  preference_id: z.string(),
  category: z.enum(['preference', 'allergy']),
  label: z.string(),
  detail: z.string().nullable(),
  /** Staff-only. Obligation G7: it never crosses the partner surface. */
  internal: z.boolean(),
  created_at: z.string(),
});
export type Preference = z.infer<typeof Preference>;

/**
 * **How long a service takes this one guest** (A1;
 * `docs/lovespa-discovery.md` §2.1 — *"your eyebrow wax takes me 15 minutes…
 * hers takes me 35"*).
 *
 * `catalog_duration_min` travels with it so a screen can draw "hers 35 ·
 * normally 15" out of one row, and so "she is back to normal" is a comparison
 * the owner can see rather than a number she has to remember.
 */
export const ClientDuration = z.object({
  service_id: z.string(),
  service_name: z.string(),
  /**
   * **Which length of this service she is remembered at** (migration 0048).
   *
   * `null` is *"this service however it is sold"* — what every row written
   * before 0048 means — and a row naming an option is the narrower fact that
   * wins over it. `BookSheet` matches on the pair and resolves that precedence
   * exactly as `effectiveDurationMin` does; the table's two partial unique
   * indexes are what guarantee there is at most one of each to choose between.
   *
   * `.catch(null).default(null)` all the same, for `request_note`'s reason: the
   * mirror does not re-parse a `clients` row it already holds, so one written
   * before these keys existed comes back without them and must read as the
   * broad row rather than failing to parse.
   */
  option_id: z.string().nullable().catch(null).default(null),
  option_name: z.string().nullable().catch(null).default(null),
  duration_min: z.number(),
  catalog_duration_min: z.number(),
  updated_by: z.string(),
  updated_at: z.string(),
});
export type ClientDuration = z.infer<typeof ClientDuration>;

export const ClientDetail = ClientSummary.extend({
  birthday: z.string().nullable(),
  notes: z.string().nullable(),
  sms_opt_in: z.boolean(),
  /**
   * **When and how the flag above came to be what it is** (migration 0041,
   * spec §2.6.1).
   *
   * A bare boolean is not consent anybody can defend: the question a business
   * is actually asked — by a guest, by a carrier, by somebody with a complaint
   * — is never *"is it true"* but *when* and *how*. So the two travel with it,
   * and the panel that draws the tick prints them.
   *
   * **`.catch(null)` on both, which is the only unusual thing here.** A row
   * this device wrote before the fields existed comes back from IndexedDB
   * without them, and a `core-api` one deploy behind sends them not at all;
   * neither is a corrupt mirror, and a schema that threw on either would empty
   * the book on the first read after a deploy. `null` is also the *honest*
   * answer in both cases — "we have no record of when" — which is a sentence
   * `SmsReminders` is built to say rather than one it has to paper over. The
   * same reasoning covers a `source` this bundle has never heard of: the value
   * degrades to "we do not know how", never to a crash on a counter.
   */
  sms_opt_in_at: z.string().nullable().catch(null),
  sms_opt_in_source: SmsOptInSource.nullable().catch(null),
  /**
   * **"Email her our newsletter"** (E3, migration 0042).
   *
   * A second, separate consent — a guest who agreed to a reminder text has not
   * agreed to marketing. `.catch(false)` for the sibling's exact reason: a row
   * this device wrote before the field existed comes back from IndexedDB
   * without it, and `false` is the honest answer for a row that never carried
   * one. It is the safe direction too: the failure mode of guessing is not
   * mailing somebody, which is the one to prefer.
   */
  email_marketing_opt_in: z.boolean().catch(false),
  /**
   * …and *when* and *how*, beside `sms_opt_in_at`/`_source` and parsed exactly
   * as they are. They existed on the column from migration 0042 and on no
   * projection until the integration pass, so the screen could show since-when
   * for one consent and not the other — which reads to an owner as "we know
   * less about this one", and was in fact "nobody wired it up".
   */
  email_marketing_opt_in_at: z.string().nullable().catch(null),
  email_marketing_opt_in_source: EmailOptInSource.nullable().catch(null),
  /**
   * **"She may book herself online"** (A5, migration 0040) — the per-client half
   * of `guest_booking: 'trusted'`.
   *
   * `core-api` has served it on this projection since A5 landed, with a comment
   * saying it is *"read by the client record's own control"*, and the client
   * record had no such control: the only way to grant trust was the one-tap
   * button on a pending request, so a regular with nothing pending could not be
   * trusted at all. `.catch(false).default(false)` for the whole family's
   * reason, and the safe direction is the same one: trust is granted, never
   * implied — least of all by a parse.
   */
  may_book_online: z.boolean().catch(false).default(false),
  preferences: z.array(Preference),
  /**
   * Her lengths, mirrored with her (A1).
   *
   * `.default([])` rather than required, because this store holds rows that were
   * written before the field existed and rows upgraded from a *summary*, and a
   * mirror that threw on its own older records would empty itself on the first
   * read after a deploy. An absent list means "none recorded", which is what the
   * booking sheet does with it anyway.
   */
  durations: z.array(ClientDuration).default([]),
  anonymized_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Echoed back on a write that carried one (`intents.ts`, `withIntentId`). */
  client_intent_id: z.string().optional(),
});
export type ClientDetail = z.infer<typeof ClientDetail>;

/**
 * The hydrate's list, **in detail** (`?detail=true`).
 *
 * The mirror asks for details rather than summaries because the device is the
 * mirror: the birthday line on Today, the preferences on the appointment tap
 * and the length the booking sheet prefills are all read out of IndexedDB, and
 * a summary carries none of them.
 *
 * **Rows already in IndexedDB are not re-parsed**, so one this device wrote
 * before `durations` existed is read back without the key even though the type
 * says it is an array. Every consumer therefore writes `client.durations ?? []`;
 * the next hydrate replaces the row and the question goes away.
 */
export const ClientList = z.object({ clients: z.array(ClientDetail) });

/** One guest's remembered lengths, the screen that edits them (A1). */
export const ClientDurationList = z.object({
  client_id: z.string(),
  durations: z.array(ClientDuration),
});
export type ClientDurationList = z.infer<typeof ClientDurationList>;

/**
 * What a `PUT`/`DELETE` of a remembered length answers with. `option_id` is
 * echoed on the option door only (0048) — the broad answer is the shape it
 * always was — so a settled intent can be matched back to the row it was about.
 */
export const ClientDurationWritten = z.object({
  client_id: z.string(),
  service_id: z.string(),
  option_id: z.string().optional(),
  duration_min: z.number().optional(),
  removed: z.boolean().optional(),
});
export type ClientDurationWritten = z.infer<typeof ClientDurationWritten>;
export const PreferenceList = z.object({ preferences: z.array(Preference) });

export const BookingSource = z.enum(['staff', 'partner', 'import', 'walk_in', 'guest_web']);
export type BookingSource = z.infer<typeof BookingSource>;

export const AppointmentStatus = z.enum([
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
  'quarantined',
  /**
   * **A5**: a time a guest asked for and the owner has not answered. It occupies
   * the room, so it is on the calendar and drawn as tentative — a day view that
   * could not name it would show an empty two o'clock she has promised away.
   */
  'requested',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

/**
 * Where an appointment sits: the primary lane, and — since migration 0056 —
 * **every other lane it occupies** (spec §4.1.2). A massage that needs a
 * person and a room is one block drawn in two columns of the day view, the
 * person's as primary. Absent is every row from before roles and every
 * service that needs one lane, so the mirror reads old rows unchanged.
 */
export const AppointmentSlot = z.object({
  start: IsoDateTime,
  end: IsoDateTime,
  resource_id: z.string(),
  also_resource_ids: z.array(z.string()).optional(),
});
export type AppointmentSlot = z.infer<typeof AppointmentSlot>;

export const Appointment = z.object({
  appointment_id: z.string(),
  status: AppointmentStatus,
  source: BookingSource,
  slot: AppointmentSlot,
  resource_name: z.string().nullable(),
  service_id: z.string(),
  service_name: z.string(),
  duration_min: z.number(),
  party_size: z.number(),
  price_cents: z.number(),
  discount_cents: z.number(),
  value_cents: z.number(),
  notes: z.string().nullable(),
  provider_ref: z.string().nullable(),
  client: z.object({
    client_id: z.string(),
    display_name: z.string(),
    phone: z.string().nullable(),
  }),
  created_at: z.string(),
  updated_at: z.string(),
  client_intent_id: z.string().optional(),
  fee_cents: z.number().optional(),
  cancelled_at: z.string().optional(),
  /**
   * The deposit already taken, when the server tells us (§7.4 rule 1 — it was
   * income on `prepaid_at`, and today's checkout carries only the remainder).
   *
   * **Optional because `core-api`'s `appointmentToWire` does not project it
   * today.** The till therefore cannot always name the amount, and it does not
   * pretend to: the checkout sends `use_deposit` without an amount, which the
   * waterfall reads as "whatever is left of it, capped at what is owed". Naming
   * a number this device does not have would be the one way to turn a missing
   * projection into a drawer that does not balance.
   */
  prepaid_cents: z.number().optional(),
  prepaid_at: z.string().nullable().optional(),
  /** A5: set exactly while `status` is `requested`; null on every booking. */
  request_expires_at: z.string().nullable().catch(null).default(null),
  /** `staff` means the owner proposed this time and is waiting on the guest. */
  request_proposed_by: z.enum(['guest', 'staff']).catch('guest').default('guest'),
  /** What the guest typed when she asked. `''` on a booking. */
  request_note: z.string().catch('').default(''),
  /** `request_declined` · `request_expired` · `''` (migration 0040). */
  cancellation_reason: z.string().catch('').default(''),
  /**
   * **Which way this one was sold, and what was attached** (migration 0048,
   * `docs/catalog-options-design.md` §4.3).
   *
   * `.catch`/`.default` for `request_note`'s reason, and it bites harder here:
   * the mirror stores appointments in IndexedDB and **does not re-parse rows it
   * already holds**, so yesterday's row comes back without these keys whatever
   * the type says. Every consumer therefore reads `appointment.add_ons ?? []`
   * rather than trusting the array — the next hydrate replaces the row and the
   * question goes away.
   *
   * `null` means the service is sold exactly one way, never "nobody chose": the
   * server resolves the default on every write path and records it.
   */
  option_id: z.string().nullable().catch(null).default(null),
  option_name: z.string().nullable().catch(null).default(null),
  /**
   * The attached lines, at the price and minutes **snapshotted when it was
   * booked** — not what the catalog says today, which is why the till reads
   * them from here rather than looking the link up again.
   */
  add_ons: z
    .array(
      z.object({
        service_id: z.string().nullable().catch(null).default(null),
        name: z.string(),
        price_cents: z.number(),
        duration_min: z.number().nullable().catch(null).default(null),
      }),
    )
    .catch([])
    .default([]),
});
export type Appointment = z.infer<typeof Appointment>;

/* ------------------------------------------------------------------ */
/* A5 — the owner's queue                                              */
/* ------------------------------------------------------------------ */

/**
 * One request as `GET /v1/requests` returns it.
 *
 * Not an `Appointment` with a different status, because the decision she is
 * being asked to make needs three facts an appointment row has no place for:
 * **who this is** (a verified telephone number, unlike the anonymous inquiry
 * list beside it), **whether she is new** — *"I need to gently bring the new
 * people in"* — and **how long the hold has left**.
 */
export const StaffRequest = z.object({
  request_id: z.string(),
  state: z.enum(['requested', 'proposed', 'confirmed', 'declined', 'expired', 'cancelled']),
  slot: AppointmentSlot,
  duration_min: z.number(),
  service_id: z.string(),
  service_name: z.string(),
  party_size: z.number(),
  value_cents: z.number(),
  note: z.string().catch('').default(''),
  proposed_by: z.enum(['guest', 'staff']).catch('guest').default('guest'),
  expires_at: z.string().nullable(),
  client: z.object({
    client_id: z.string(),
    display_name: z.string().catch('').default(''),
    phone: z.string().nullable().catch(null).default(null),
    verified: z.boolean().catch(true).default(true),
    is_new: z.boolean().catch(false).default(false),
    may_book_online: z.boolean().catch(false).default(false),
  }),
  created_at: z.string(),
  /** `queued` · `no_consent` · `no_phone` — what became of the guest's text. */
  notice: z.string().optional(),
  client_intent_id: z.string().optional(),
});
export type StaffRequest = z.infer<typeof StaffRequest>;

export const StaffRequestList = z.object({
  requests: z.array(StaffRequest),
  as_of: z.string(),
});
export type StaffRequestList = z.infer<typeof StaffRequestList>;

export const AppointmentDay = z.object({
  date_from: IsoDate,
  date_to: IsoDate,
  timezone: z.string(),
  appointments: z.array(Appointment),
});
export type AppointmentDay = z.infer<typeof AppointmentDay>;

/**
 * **An item's photographs** (B7) — what `GET /v1/products` and
 * `GET /v1/services` carry per row, in the owner's order, the first being the
 * card's.
 *
 * **Absent and `[]` are different, and the mirror depends on it.** The lists
 * and by-id reads always say (`[]` for none); every write's echo — a price edit,
 * a retire — does not, because it did not read them. `putProduct` and
 * `putService` keep the stored list when a row arrives without one, so a
 * rename does not wipe the thumbnails. `.catch(undefined)` rather than
 * `.catch([])` for exactly that reason: a malformed list is "not said", not
 * "none".
 */
export const ItemImage = z.object({
  media_id: z.string(),
  url: z.string(),
  alt: z.string().catch(''),
  /**
   * **On the website** (B10, migration 0072). The staff wire carries every
   * photograph and marks the ones a stranger does not see. `.catch(true)` and
   * defaulted: a row mirrored before the field existed was on the website,
   * because every photograph was.
   */
  on_website: z.boolean().catch(true).default(true),
});
export type ItemImage = z.infer<typeof ItemImage>;
export const ItemImages = z.array(ItemImage).optional().catch(undefined);

/** `POST /v1/{products|services}/:id/photos/upload-url` — a signed PUT, nothing written. */
export const ItemPhotoUpload = z.object({
  upload_id: z.string(),
  url: z.string(),
  content_type: z.string(),
  max_bytes: z.number(),
  expires_at: z.string(),
});
export type ItemPhotoUpload = z.infer<typeof ItemPhotoUpload>;

/** The attach and arrange answers: the item's list as it now stands. */
export const ItemPhotos = z.object({
  item_id: z.string(),
  images: z.array(ItemImage),
  catalog_version: z.number().optional(),
});

export const Service = z.object({
  service_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /**
   * Her menu's heading (`null` = none) and her order — what groups and orders
   * the Settings menu like the card she hands guests. `.catch` for the usual
   * reason: rows mirrored before the fields existed are not re-parsed, so
   * readers use `?? null` / `?? 0` too.
   */
  category: z.string().nullable().catch(null).default(null),
  sort_order: z.number().catch(0).default(0),
  price_cents: z.number(),
  duration_min: z.number(),
  /**
   * `null` is "the house decides" (migration 0064) and `0` is none; the
   * calendar resolves both through `buffersFor` with the session's
   * `scheduling` defaults beside them. `.catch(null)` because a `services` row
   * mirrored before the column could say so comes back as a number, and a
   * number parses; only garbage falls to the house.
   */
  buffer_before_min: z.number().nullable().catch(null),
  buffer_after_min: z.number().nullable().catch(null),
  max_party: z.number(),
  active: z.boolean(),
  bookable_by_partner: z.boolean(),
  bookable_online: z.boolean(),
  details: z.array(z.string()),
  resource_ids: z.array(z.string()),
  /**
   * **Each link with its role** (spec §4.1.2, migration 0056) — what the Team
   * screen's "needs one person and one room" toggle reads and writes, and what
   * the booking sheet draws a second `<select>` from. `.catch([])` because a
   * `services` row mirrored before the field existed comes back from IndexedDB
   * without it; an empty list reads as "every link is `any`", which is what
   * `resource_ids` alone always meant.
   */
  resources: z
    .array(z.object({ resource_id: z.string(), role: z.enum(['any', 'person', 'room']) }))
    .catch([])
    .default([]),
  /**
   * **"Never sold on its own"** (migration 0048). Attach-only treatments stay
   * *on* this wire — the desk is where they are edited and priced — so the
   * booking sheet is what keeps them out of the standalone picker.
   */
  attach_only: z.boolean().catch(false).default(false),
  /**
   * The ways this service is sold, and what may be attached to it.
   *
   * On the staff wire an **empty array is meaningful**: "no lengths yet, draw
   * the add button". `.default([])` therefore has to mean the same thing as the
   * server's `[]`, which it does — the only case it covers is a row this device
   * mirrored before the fields existed, and such a tenant has authored none.
   *
   * `.catch`/`.default` throughout for `sort_order`'s reason: a `services` row
   * already in IndexedDB is **not re-parsed**, so consumers read
   * `service.options ?? []` rather than trusting the array.
   */
  default_option_id: z.string().nullable().catch(null).default(null),
  options: z
    .array(
      z.object({
        option_id: z.string(),
        ref: z.string().catch('').default(''),
        name: z.string(),
        price_cents: z.number(),
        /** Absent when the option does not change the span — a dish's *Large*. */
        duration_min: z.number().nullable().catch(null).default(null),
        is_default: z.boolean().catch(false).default(false),
        active: z.boolean().catch(true).default(true),
        sort_order: z.number().catch(0).default(0),
      }),
    )
    .catch([])
    .default([]),
  add_ons: z
    .array(
      z.object({
        service_id: z.string(),
        name: z.string(),
        /** The **attached** numbers — what it costs and adds *on this parent*. */
        price_cents: z.number(),
        duration_min: z.number(),
        active: z.boolean().catch(true).default(true),
        sort_order: z.number().catch(0).default(0),
      }),
    )
    .catch([])
    .default([]),
  /** The treatment's photographs (B7) — `ItemImages`' rule: absent is "not said". */
  images: ItemImages,
  catalog_version: z.number().optional(),
  client_intent_id: z.string().optional(),
});
export type Service = z.infer<typeof Service>;

export const ServiceList = z.object({ services: z.array(Service) });

export const Resource = z.object({
  resource_id: z.string(),
  name: z.string(),
  kind: z.enum(['room', 'person', 'equipment', 'table_pool']),
  capacity: z.number(),
  units: z.number(),
  active: z.boolean(),
  /**
   * The owner's own order (S2). `.catch(0)` for the same reason every other
   * late-added field here has one: a device holding a mirrored row written by
   * the build before this one must not fail to parse it — it reads as "nobody
   * has reordered anything", which is exactly what the server's default means.
   */
  sort_order: z.number().catch(0).default(0),
  hours: z.array(DayHours).nullable(),
  /** The staff login this person is, or `null` (S2). */
  staff_user_id: z.string().nullable().catch(null).default(null),
  catalog_version: z.number().optional(),
  client_intent_id: z.string().optional(),
});
export type Resource = z.infer<typeof Resource>;

export const ResourceList = z.object({ resources: z.array(Resource) });

/**
 * A staff login, and the person on the calendar it is (S2, `GET /v1/team/seats`).
 *
 * **Not mirrored.** `useRemote`'s rule rather than `useLive`'s: the list changes
 * only when an operator provisions an account, nothing on this screen queues a
 * write against it, and a device in a tunnel needs to know who is *booked*, not
 * who could sign in.
 */
export const TeamSeat = z.object({
  staff_user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: z.enum(['owner', 'staff', 'readonly']),
  active: z.boolean(),
  resource_id: z.string().nullable(),
});
export type TeamSeat = z.infer<typeof TeamSeat>;

export const TeamSeatList = z.object({ seats: z.array(TeamSeat) });

export const Hours = z.object({ timezone: z.string(), hours: z.array(DayHours) });
export type Hours = z.infer<typeof Hours>;

export const HoursWritten = z.object({
  hours: z.array(DayHours),
  client_intent_id: z.string().optional(),
});

/**
 * A booking sitting inside a closure — a telephone call the owner has to make
 * (S2; spec §8.3, and CLAUDE.md's locked rule that conflicts surface as owner
 * tasks and are never merged).
 *
 * Nothing on this device cancels one, and there is deliberately no control on
 * the Team screen that does: the two ways out are to move the appointment or to
 * shorten the time off, and both of those are things the owner decides.
 */
export const TimeOffConflict = z.object({
  appointment_id: z.string(),
  resource_id: z.string(),
  start: IsoDateTime,
  end: IsoDateTime,
  client_name: z.string(),
  service_name: z.string(),
  status: z.string(),
});
export type TimeOffConflict = z.infer<typeof TimeOffConflict>;

export const Blackout = z.object({
  blackout_id: z.string(),
  resource_id: z.string().nullable(),
  start: IsoDateTime,
  end: IsoDateTime,
  reason: z.string().nullable(),
  /**
   * `.catch([])` — a row mirrored by an older build parses as "nothing to ring
   * about" rather than failing the whole list.
   *
   * `.optional()` rather than `.default([])`, and the difference is only in the
   * *type*: `core-api` always sends the field, but making it required here would
   * make every hand-built `Blackout` literal in the app's own suites — including
   * the calendar's, which this wave does not own — a compile error for a field
   * none of them is about. Readers take `?? []`.
   */
  conflicts: z.array(TimeOffConflict).catch([]).optional(),
  client_intent_id: z.string().optional(),
});
export type Blackout = z.infer<typeof Blackout>;

export const BlackoutList = z.object({ blackouts: z.array(Blackout) });

export const Deleted = z.object({
  deleted: z.boolean(),
  client_intent_id: z.string().optional(),
});

export const OfferedSlot = z.object({
  start: IsoDateTime,
  end: IsoDateTime,
  resource_id: z.string(),
  service_id: z.string(),
  capacity: z.number(),
});
export type OfferedSlot = z.infer<typeof OfferedSlot>;

export const Availability = z.object({
  authority: z.enum(['live', 'approximate']),
  slots: z.array(OfferedSlot),
  alternatives: z.array(OfferedSlot),
  summary: z.string(),
});
export type Availability = z.infer<typeof Availability>;

/**
 * The `409` that becomes an owner task (spec §8.3).
 *
 * `source` is the field the copy hangs on. "The concierge sold this while you
 * were offline" and "you booked this on the other iPad" are different sentences
 * and `enrichConflict` in `routes/appointments.ts` is what makes the difference
 * knowable; parsing it loosely here would throw that away.
 */
export const ConflictingAppointment = z.object({
  appointment_id: z.string(),
  status: z.string(),
  source: BookingSource,
  client_name: z.string().nullable(),
  service_name: z.string().nullable(),
  start: z.string(),
  end: z.string(),
});
export type ConflictingAppointment = z.infer<typeof ConflictingAppointment>;

export const SlotTakenDetail = z.object({
  reason: z.literal('slot_taken'),
  client_intent_id: z.string().optional(),
  resource_id: z.string(),
  requested: z.object({ start: z.string(), end: z.string() }),
  conflicts: z.array(ConflictingAppointment),
});
export type SlotTakenDetail = z.infer<typeof SlotTakenDetail>;

/* ------------------------------------------------------------------ */
/* MVP 4 — the money surface (spec §6.5, §7.3, §7.4)                    */
/* ------------------------------------------------------------------ */

/**
 * The tender vocabulary, mirrored from `packages/store`'s `SettlementTenderKind`.
 *
 * **Gift credit is deliberately absent**, here as there. §7.4 rule 2 makes it a
 * *discount* when spent, never a payment; it lands in `discount_cents` and shows
 * on the till as a discount line. A screen that listed it beside `cash` would be
 * teaching the owner that goodwill is revenue, which is the exact confusion the
 * rule exists to prevent.
 */
export const TenderKind = z.enum([
  'deposit',
  'certificate',
  'credit_cash',
  'cash',
  'check',
  'venmo',
  'paypal',
  'card',
  'other',
]);
export type TenderKind = z.infer<typeof TenderKind>;

export const SettlementTender = z.object({
  kind: TenderKind,
  amount_cents: z.number(),
  ref: z.string().optional(),
  /** The owner's own word for an `other` tender — "Zelle". Display text. */
  label: z.string().optional(),
});
export type SettlementTender = z.infer<typeof SettlementTender>;

export const SettlementLine = z.object({
  line_id: z.string(),
  item_id: z.string().nullable(),
  item_name: z.string(),
  qty: z.number(),
  /** The **list** price (§7.4 rule 3) — what it would have cost. */
  unit_price_cents: z.number(),
  discount_cents: z.number(),
  charged_cents: z.number(),
  /**
   * "coral scarf" — what a generic open-price line actually was (B4).
   *
   * `.catch('')` rather than required: it is additive, and a receipt that would
   * not render because a deployment one revision behind omitted a note would be
   * a worse failure than a missing two words.
   */
  note: z.string().catch('').default(''),
});
export type SettlementLine = z.infer<typeof SettlementLine>;

/**
 * The server's own account of which step of the waterfall took what.
 *
 * The till previews the same arithmetic locally (`money/waterfall.ts`) so the
 * owner can watch the total move as they type. This is what it is checked
 * against the moment the document lands: the preview is a courtesy, the server
 * is the ledger.
 */
export const SettlementApplied = z.object({
  line_discount_cents: z.number(),
  manual_discount_cents: z.number(),
  certificate_cents: z.number(),
  deposit_cents: z.number(),
  /** A discount, not a tender — §7.4 rule 2, made visible. */
  gift_credit_cents: z.number(),
  cash_credit_cents: z.number(),
  /** The sale's rest **plus the tip** — what the external tenders settled (0046). */
  remainder_cents: z.number(),
  /**
   * The **first** part's kind, which for a single tender is exactly what this
   * field has always carried. `remainders` below is the whole answer.
   */
  remainder_kind: RemainderKind,
  /** Echoed only for an `other` remainder that carried one (B4). */
  remainder_label: z.string().optional(),
  /**
   * **The split, itemized** (0046). `.catch([])` rather than required for
   * `note`'s reason: it is additive, and a receipt that would not render
   * because the deployment is one revision behind would be the worse failure.
   */
  remainders: z.array(SettlementTender).catch([]).default([]),
  /** Money for a person, never the shop's. `Σ tenders = total + tip`. */
  tip_cents: z.number().catch(0).default(0),
});
export type SettlementApplied = z.infer<typeof SettlementApplied>;

export const Settlement = z.object({
  settlement_id: z.string(),
  client_id: z.string().nullable(),
  client_name: z.string().nullable(),
  appointment_id: z.string().nullable(),
  /** **The date of money** (§7.4 rule 1). */
  settled_on: IsoDate,
  subtotal_cents: z.number(),
  discount_cents: z.number(),
  tax_cents: z.number(),
  total_cents: z.number(),
  /**
   * **A tip, beside the total and not inside it** (0046): `total_cents` is the
   * sale and `Σ tenders = total_cents + tip_cents`. `0` on every document
   * written before the column existed, which is what makes the old reading and
   * the new one identical on all of them.
   */
  tip_cents: z.number().catch(0).default(0),
  tenders: z.array(SettlementTender),
  /**
   * Derived by the server: the part of this document that is income *today* —
   * and a tip is not, so it is already subtracted.
   */
  new_money_cents: z.number(),
  /** What later refund documents have taken off this sale (0046). */
  refunded_cents: z.number().catch(0).default(0),
  refunded_tip_cents: z.number().catch(0).default(0),
  created_at: z.string(),
  lines: z.array(SettlementLine).optional(),
  applied: SettlementApplied.optional(),
  client_intent_id: z.string().optional(),
});
export type Settlement = z.infer<typeof Settlement>;

export const SettlementList = z.object({ settlements: z.array(Settlement) });

/* ------------------------------------------------------------------ */
/* Refunds (migration 0046)                                            */
/* ------------------------------------------------------------------ */

/**
 * How a refund went back — the six recorded external tenders, plus two that
 * keep the money with the shop.
 *
 * `credit_gift` returns goodwill (§7.4 rule 2: it was never income, so handing
 * back cash for it would turn a giveaway into a withdrawal) and `credit_cash`
 * puts real money back on the account.
 */
export const RefundTenderKind = z.enum([
  'cash',
  'check',
  'venmo',
  'paypal',
  'card',
  'other',
  'credit_cash',
  'credit_gift',
]);
export type RefundTenderKind = z.infer<typeof RefundTenderKind>;

export const RefundTender = z.object({
  kind: RefundTenderKind,
  amount_cents: z.number(),
  label: z.string().optional(),
});
export type RefundTender = z.infer<typeof RefundTender>;

export const Refund = z.object({
  refund_id: z.string(),
  settlement_id: z.string().nullable(),
  certificate_id: z.string().nullable(),
  client_id: z.string().nullable(),
  client_name: z.string().nullable(),
  /** What comes off revenue, on **its own** day (§7.4 rule 1). */
  amount_cents: z.number(),
  tip_cents: z.number().catch(0).default(0),
  tenders: z.array(RefundTender),
  money_out_cents: z.number().catch(0).default(0),
  reason: z.string().catch('').default(''),
  refunded_on: IsoDate,
  created_at: z.string(),
  lines: z
    .array(
      z.object({
        line_id: z.string(),
        settlement_line_id: z.string().nullable(),
        item_id: z.string().nullable(),
        item_name: z.string(),
        qty: z.number(),
        amount_cents: z.number(),
        restocked: z.boolean(),
      }),
    )
    .catch([])
    .default([]),
  client_intent_id: z.string().optional(),
});
export type Refund = z.infer<typeof Refund>;

export const RefundList = z.object({ refunds: z.array(Refund) });

/**
 * **What a refund is allowed to be** — the server's own caps, for the screen
 * that is about to offer one.
 *
 * It is fetched rather than derived from the settlement on screen because the
 * cap is `total − Σ prior refunds` and the prior refunds are not in the mirror.
 * That is also why the refund sheet refuses to open offline and says so: a
 * guess here is a document the ledger rejects after somebody has been told they
 * have their money back.
 */
export const RefundableSettlement = z.object({
  settlement_id: z.string(),
  client_id: z.string().nullable(),
  settled_on: IsoDate,
  total_cents: z.number(),
  tip_cents: z.number(),
  refundable_cents: z.number(),
  refundable_tip_cents: z.number(),
  lines: z.array(
    z.object({
      line_id: z.string(),
      item_id: z.string().nullable(),
      item_name: z.string(),
      qty: z.number(),
      charged_cents: z.number(),
      refunded_cents: z.number(),
    }),
  ),
});
export type RefundableSettlement = z.infer<typeof RefundableSettlement>;

/** `voided` is a refunded certificate (0046) — not "spent", which is the point. */
export const CertificateState = z.enum(['open', 'redeemed', 'expired', 'voided']);
export type CertificateState = z.infer<typeof CertificateState>;

/**
 * A gift certificate.
 *
 * `state` arrives computed against the server's `today` and is **recomputed on
 * this device** whenever it is displayed (`money/certificates.ts`), for the
 * reason `routes/certificates.ts` gives for computing it at all: expiry is
 * derived, never a column. A mirrored `state` that was fetched last Tuesday
 * would tell the counter a dead certificate is live.
 */
export const Certificate = z.object({
  certificate_id: z.string(),
  code: z.string(),
  value_cents: z.number(),
  applied_cents: z.number(),
  remaining_cents: z.number(),
  item_id: z.string().nullable(),
  item_name: z.string().nullable(),
  /**
   * Which length the card names (migration 0054) — "the 90-minute facial"
   * rather than "a facial". `.catch`/`.default` for `voided_at`'s reason: a row
   * mirrored before the field existed is not re-parsed, and it named none.
   */
  option_id: z.string().nullable().catch(null).default(null),
  option_name: z.string().nullable().catch(null).default(null),
  purchaser_client_id: z.string().nullable(),
  purchaser_name: z.string().nullable(),
  /**
   * **Who holds the card, as a client** (migration 0074). `null` for a card
   * from before the column, an import, or one mirrored before the field — the
   * `.catch`/`.default` for `voided_at`'s reason. `recipient_name` is the
   * snapshot of her name at issue and is what the list prints either way.
   */
  recipient_client_id: z.string().nullable().catch(null).default(null),
  recipient_name: z.string(),
  issued_on: IsoDate,
  expires_on: IsoDate.nullable(),
  state: CertificateState,
  redeemed_at: z.string().nullable(),
  redeemed_settlement_id: z.string().nullable(),
  /** Refunded, and therefore void (0046) — a different fact from redeemed. */
  voided_at: z.string().nullable().catch(null).default(null),
  /**
   * **How the card was paid for** (migration 0070) — one of the till's six, or
   * `null` for a card sold before anybody asked. `.catch`/`.default` for
   * `voided_at`'s reason: a mirrored row from before the field is not
   * re-parsed, and an unknown seventh word is "not asked" rather than a crash.
   */
  paid_with: RemainderKind.nullable().catch(null).default(null),
  /** The owner's word for an `other`; `null` on every other kind. */
  paid_with_label: z.string().nullable().catch(null).default(null),
  created_at: z.string(),
  client_intent_id: z.string().optional(),
});
export type Certificate = z.infer<typeof Certificate>;

export const CertificateList = z.object({
  as_of: IsoDate,
  certificates: z.array(Certificate),
});

export const CreditEntry = z.object({
  credit_entry_id: z.string(),
  client_id: z.string(),
  /** `cash` is money, `gift` is goodwill. Never collapsed (§7.4 rule 2). */
  kind: z.enum(['cash', 'gift']),
  amount_cents: z.number(),
  reason: z.string(),
  settlement_id: z.string().nullable(),
  occurred_on: IsoDate,
  created_at: z.string(),
});
export type CreditEntry = z.infer<typeof CreditEntry>;

export const CreditBalances = z.object({
  cash_cents: z.number(),
  gift_cents: z.number(),
});
export type CreditBalances = z.infer<typeof CreditBalances>;

export const ClientCredit = z.object({
  client_id: z.string(),
  balances: CreditBalances,
  entries: z.array(CreditEntry),
});
export type ClientCredit = z.infer<typeof ClientCredit>;

/** The response to `POST /v1/clients/{id}/credit` — the entry and the new balance. */
export const CreditAdded = CreditEntry.extend({
  balances: CreditBalances,
  /** Rule 2, stated by the server rather than inferred here. */
  income: z.boolean(),
  client_intent_id: z.string().optional(),
});
export type CreditAdded = z.infer<typeof CreditAdded>;

/** `GET /v1/credit/outstanding` — every balance the shop owes, in one read. */
export const CreditOutstanding = z.object({
  totals: CreditBalances,
  clients: z.array(
    z.object({
      client_id: z.string(),
      display_name: z.string(),
      cash_cents: z.number(),
      gift_cents: z.number(),
    }),
  ),
});
export type CreditOutstanding = z.infer<typeof CreditOutstanding>;

export const Product = z.object({
  product_id: z.string(),
  name: z.string(),
  description: z.string(),
  price_cents: z.number(),
  tax_class: z.string(),
  brand: z.string(),
  notes: z.string(),
  active: z.boolean(),
  sort_order: z.number(),
  /**
   * **The cashier types the price** (`items.open_price`, B4).
   *
   * `price_cents` above is a placeholder for such a line, not a claim: the till
   * asks for an amount per line and records a discount of zero. `.catch(false)`
   * and defaulted, because it is additive — a `core-api` one deploy behind, or a
   * row this device mirrored before the field existed, means "an ordinary
   * product", which is what every product was yesterday.
   */
  open_price: z.boolean().catch(false).default(false),
  /**
   * **Show on the website** (B10, migration 0072). Off keeps it on the till and
   * off the shop page. Defaulted for `open_price`'s reason: yesterday every
   * product was on the website.
   */
  on_website: z.boolean().catch(true).default(true),
  /** The product's photographs (B7) — `ItemImages`' rule: absent is "not said". */
  images: ItemImages,
  catalog_version: z.number().optional(),
  client_intent_id: z.string().optional(),
});
export type Product = z.infer<typeof Product>;

export const ProductList = z.object({ products: z.array(Product) });

/** `PUT /v1/settings/catalog-editors` (B10) — the owner's answer, echoed. */
export const CatalogEditorsAnswer = z.object({
  catalog_editors: z.enum(['owner', 'owner_and_staff']),
  changed: z.boolean(),
});

/* ---- the reports (module `reporting`) ---------------------------- */

const RevenueBySource = z.object({
  settlement_cents: z.number(),
  deposit_cents: z.number(),
  certificate_cents: z.number(),
  cash_credit_topup_cents: z.number(),
  /**
   * **Negative** (0046) — money given back, on the refund's own date, so the
   * total is net revenue. Caught and defaulted because it is additive: a
   * `core-api` one deploy behind answers without it.
   */
  refund_cents: z.number().catch(0).default(0),
});
export type RevenueBySource = z.infer<typeof RevenueBySource>;

export const ReportSummary = z.object({
  as_of: IsoDate,
  timezone: z.string(),
  date_from: IsoDate,
  date_to: IsoDate,
  revenue_cents: z.number(),
  by_source: RevenueBySource,
  /**
   * **How the counter was paid** (B4) — `by_source.settlement_cents` split
   * across the six recorded tenders, and summing back to it.
   *
   * A `Record` keyed by the tender rather than six named fields, matching the
   * server: the set is the enum, and a second hand-written list of it here is
   * the copy that drifts the first time a seventh way of being paid is real.
   *
   * Keyed by `z.string()` rather than by `RemainderKind`, and that is not
   * laziness: a zod record over an enum demands **every** key, which would make
   * a server that has learned a seventh tender — or one that omitted a zero —
   * fail the whole summary. The reader walks `TENDER_ORDER` and takes `?? 0`, so
   * an unknown key is ignored and a missing one is nothing, which is what both
   * of them mean.
   *
   * Defaulted and caught, because it is additive: a `core-api` one deploy behind
   * answers without it and the panel simply does not draw.
   */
  by_tender: z.record(z.string(), z.number()).catch({}).default({}),
  /**
   * **Gift cards sold in the window, by how they were paid** (migration 0070).
   *
   * Beside `by_tender`, never inside it — the server keeps the counter's
   * equation (`Σ by_tender − tip = by_source.settlement_cents`) intact — and
   * added to it per tender by the Today line, which is the one place the owner
   * asked to see the two together. Same record shape and the same `.catch`/
   * `.default` for the same reason: a `core-api` one deploy behind omits it,
   * and "no gift cards by tender" is exactly what that means.
   */
  certificate_by_tender: z.record(z.string(), z.number()).catch({}).default({}),
  /** **Money out** (0046), by the tender it went back through. Positive. */
  by_tender_refunded: z.record(z.string(), z.number()).catch({}).default({}),
  discount_cents: z.number(),
  /**
   * **Tips** (0046): inside `by_tender` because they are in the drawer, and
   * outside `by_source.settlement_cents` because they are not the shop's.
   */
  tip_cents: z.number().catch(0).default(0),
  refunded_tip_cents: z.number().catch(0).default(0),
  /** Money held for a government. `0` on every tenant in the USVI. */
  tax_cents: z.number().catch(0).default(0),
  refund_cents: z.number().catch(0).default(0),
  refunds: z.number().catch(0).default(0),
  settlements: z.number(),
  visits: z.number(),
  no_shows: z.number(),
  upcoming: z.number(),
});
export type ReportSummary = z.infer<typeof ReportSummary>;

export const RevenueByMonth = z.object({
  as_of: IsoDate,
  timezone: z.string(),
  month_from: z.string(),
  month_to: z.string(),
  months: z.array(
    z.object({
      month: z.string(),
      total_cents: z.number(),
      by_source: RevenueBySource,
    }),
  ),
  total_cents: z.number(),
  by_source: RevenueBySource,
  discount_cents: z.number(),
});
export type RevenueByMonth = z.infer<typeof RevenueByMonth>;

export const ServiceMix = z.object({
  as_of: IsoDate,
  date_from: IsoDate,
  date_to: IsoDate,
  kind: z.string(),
  items: z.array(
    z.object({
      item_id: z.string().nullable(),
      name: z.string(),
      kind: z.string(),
      lines: z.number(),
      qty: z.number(),
      list_cents: z.number(),
      discount_cents: z.number(),
      net_cents: z.number(),
      /**
       * **The lengths this treatment was sold at** (migration 0052, the owner's
       * ruling 3): one heading, the options as sub-rows.
       *
       * `.catch([])` rather than `.optional()`, and the difference matters on a
       * PWA that is deliberately allowed to be a deploy behind the API: a tablet
       * holding yesterday's bundle against today's server, or today's bundle
       * against a server that has not shipped yet, draws the headings either
       * way. A mix report that refused to render because a sub-row arrived in a
       * shape it did not expect would take the whole panel down for a detail.
       * `.default([])` beside it for the key that is simply absent, which is the
       * pair `option_id` above already uses.
       */
      options: z
        .array(
          z.object({
            option_id: z.string(),
            name: z.string(),
            lines: z.number(),
            qty: z.number(),
            list_cents: z.number(),
            discount_cents: z.number(),
            net_cents: z.number(),
          }),
        )
        .catch([])
        .default([]),
    }),
  ),
});
export type ServiceMix = z.infer<typeof ServiceMix>;

export const VisitsByWeekday = z.object({
  as_of: IsoDate,
  date_from: IsoDate,
  date_to: IsoDate,
  weekdays: z.array(z.object({ weekday: z.number(), name: z.string(), visits: z.number() })),
});
export type VisitsByWeekday = z.infer<typeof VisitsByWeekday>;

export const TopClients = z.object({
  as_of: IsoDate,
  date_from: IsoDate,
  date_to: IsoDate,
  clients: z.array(
    z.object({
      client_id: z.string(),
      display_name: z.string(),
      visits: z.number(),
      revenue_cents: z.number(),
      discount_cents: z.number(),
      last_visit_on: IsoDate.nullable(),
    }),
  ),
});
export type TopClients = z.infer<typeof TopClients>;

export const LapsedRegulars = z.object({
  as_of: IsoDate,
  clients: z.array(
    z.object({
      client_id: z.string(),
      display_name: z.string(),
      phone: z.string().nullable(),
      visits: z.number(),
      usual_gap_days: z.number(),
      days_since: z.number(),
      last_visit_on: IsoDate.nullable(),
    }),
  ),
});
export type LapsedRegulars = z.infer<typeof LapsedRegulars>;

/* ------------------------------------------------------------------ */
/* The online surfaces — ordering, departures, lodging, pay links       */
/* ------------------------------------------------------------------ */

/**
 * These five schemas describe rows that are **never written to the mirror**.
 *
 * The offline boundary in this app is the *book* (spec §8.1: today plus a
 * window of appointments, the catalog, the clients). A kitchen pass and a hotel
 * front desk are not that: they are rooms with a router in them, and the thing
 * that would go wrong offline is not staleness but a lie — a ticket lane drawn
 * from a cached order says "confirmed" about food that was collected an hour
 * ago, and `next_statuses` cached with it draws a button the domain would now
 * refuse. So the screens that consume these fetch, show what they fetched, and
 * stamp when they fetched it. See `ui/useRemote.ts`.
 */

export const OrderStatus = z.enum([
  'placed',
  'confirmed',
  'preparing',
  'ready',
  'completed',
  'cancelled',
]);
export type OrderStatus = z.infer<typeof OrderStatus>;

/**
 * The fulfilled lane (migration 0026): every value written by a machine — a
 * capture, the submission drain, a shipment notice — and none by a person, so
 * the Orders board's verbs never name one. `delivered` is in the enum and
 * written by nothing; the Parcels board says why.
 */
export const ParcelStatus = z.enum(['paid', 'submitted', 'submit_failed', 'shipped', 'delivered']);
export type ParcelStatus = z.infer<typeof ParcelStatus>;

/** Anything an order can be — what a *read* may carry (the kitchen writes only `OrderStatus`). */
export const AnyOrderStatus = z.union([OrderStatus, ParcelStatus]);
export type AnyOrderStatus = z.infer<typeof AnyOrderStatus>;

export const OrderSource = z.enum(['staff', 'guest_web', 'partner']);
export type OrderSource = z.infer<typeof OrderSource>;

export const OrderLine = z.object({
  line_id: z.string(),
  item_id: z.string(),
  /** Snapshotted at order time, one language (migration 0012, §7.4). */
  item_name: z.string(),
  /**
   * **Which size** (migration 0055) — the name as the server resolves it
   * today, printed beside the dish because "Poke bowl" and "Poke bowl ·
   * Large" are two different things to make. `.default(null)` for `notes`'
   * reason: a PWA one release ahead of its service still renders a ticket.
   */
  option_id: z.string().nullable().default(null),
  option_name: z.string().nullable().default(null),
  qty: z.number(),
  unit_price_cents: z.number(),
  line_total_cents: z.number(),
  /**
   * **What the guest asked for about this dish** — "no onions" (migration
   * 0034). `.default('')` so a PWA one release ahead of its service still
   * renders a ticket rather than refusing to parse one.
   */
  notes: z.string().default(''),
  modifiers: z.array(
    z.object({
      modifier_id: z.string(),
      name: z.string(),
      price_delta_cents: z.number(),
    }),
  ),
});
export type OrderLine = z.infer<typeof OrderLine>;

export const Order = z.object({
  order_id: z.string(),
  client_id: z.string().nullable(),
  client_name: z.string().nullable(),
  // A merch order shares the table and sits in the fulfilled lane; a board
  // that refused to parse it would refuse the whole day's read.
  status: AnyOrderStatus,
  source: OrderSource,
  subtotal_cents: z.number(),
  tax_cents: z.number(),
  total_cents: z.number(),
  prepaid_cents: z.number(),
  prepaid_at: z.string().nullable(),
  outstanding_cents: z.number(),
  /**
   * **The buttons.** Derived by the server from `ORDER_TRANSITIONS`, which is
   * why this screen never carries a transition table: a second copy of it on a
   * tablet is a second answer to "may this move", and the tablet's copy is the
   * one that is out of date. `orderToWire` says the same thing from its side.
   */
  next_statuses: z.array(AnyOrderStatus),
  pickup_at: z.string().nullable(),
  notes: z.string().nullable(),
  /**
   * **What the kitchen said it would take**, in minutes from `confirmed`
   * (migration 0057), or `null` for "nobody said". `.default(null)` for
   * `notes`' reason: a PWA one release ahead of its service still renders a
   * ticket. `0` is "ready now" and is printed; `null` prints nothing.
   */
  prep_minutes: z.number().nullable().default(null),
  placed_at: z.string(),
  ready_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  lines: z.array(OrderLine),
  client_intent_id: z.string().optional(),
});
export type Order = z.infer<typeof Order>;

export const OrderList = z.object({ orders: z.array(Order) });

/**
 * **The 86 board** — `GET /v1/menu` as the pass reads it (`docs/verticals-plan.md`
 * V1 / R3): every dish and every choice with its `sold_out`, plus the house's
 * `OrderingPolicy`, which the Confirm control prefills its prep time from. The
 * policy rides this read rather than `/session` because it is the one read the
 * Orders screen makes before it draws a Confirm button.
 */
export const StaffMenuModifier = z.object({
  modifier_id: z.string(),
  name: z.string(),
  price_delta_cents: z.number(),
  sold_out: z.boolean(),
});
export const StaffMenuItem = z.object({
  item_id: z.string(),
  name: z.string(),
  category: z.string(),
  price_cents: z.number(),
  active: z.boolean(),
  sold_out: z.boolean(),
  groups: z.array(
    z.object({ group_id: z.string(), name: z.string(), modifiers: z.array(StaffMenuModifier) }),
  ),
});
export type StaffMenuItem = z.infer<typeof StaffMenuItem>;
export const StaffMenu = z.object({
  catalog_version: z.number(),
  policy: z.object({
    order_confirmation: z.enum(['staff_confirm', 'auto_confirm']).catch('staff_confirm'),
    default_prep_minutes: z.number().nullable().catch(null),
  }),
  items: z.array(StaffMenuItem),
});
export type StaffMenu = z.infer<typeof StaffMenu>;

/** What the two sold-out verbs answer: the row's new state and the version it moved to. */
export const MenuSoldOutResult = z.object({
  sold_out: z.boolean(),
  changed: z.boolean(),
  catalog_version: z.number(),
});

/**
 * A parcel — an order in the fulfilled lane with its satellite, as
 * `fulfillmentToWire(record, 'staff')` projects it. The submission state
 * (`attempts`, `next_attempt_at`, `last_error`) is what makes `submit_failed`
 * actionable rather than decorative.
 */
export const ParcelShipment = z.object({
  carrier: z.string(),
  tracking_number: z.string(),
  tracking_url: z.string(),
});
export const Parcel = z.object({
  order_id: z.string(),
  status: AnyOrderStatus,
  placed_at: z.string(),
  shipping_service: z.string(),
  shipping_cents: z.number(),
  submitted_at: z.string().nullable(),
  tracking: z.object({
    provider_status: z.string(),
    shipments: z.array(ParcelShipment),
  }),
  provider: z.string(),
  provider_order_id: z.string().nullable(),
  confirmed: z.boolean(),
  attempts: z.number(),
  next_attempt_at: z.string().nullable(),
  last_error: z.string().nullable(),
  total_cents: z.number(),
  prepaid_cents: z.number(),
  recipient: z.object({ name: z.string(), city: z.string().optional() }).passthrough(),
  items: z.array(z.object({ name: z.string().default(''), quantity: z.number() }).passthrough()),
});
export type Parcel = z.infer<typeof Parcel>;
export const ParcelList = z.object({ parcels: z.array(Parcel) });

/* ------------------------------------------------------------------ */
/* Data requests — /v1/data-requests (spec §7.6, GS5, migration 0061)   */
/* ------------------------------------------------------------------ */

/**
 * A guest's ask for an export or a deletion, as the desk's queue reads it.
 * `days_left` is the server's arithmetic on the thirty-day clock — negative is
 * overdue, `null` once the task is closed — so this app never knows the rule.
 */
export const DataRequest = z.object({
  data_request_id: z.string(),
  guest_ref: z.string(),
  guest_name: z.string(),
  kind: z.enum(['export', 'delete']),
  status: z.enum(['open', 'done', 'cancelled']),
  requested_by: z.string(),
  created_at: z.string(),
  due_at: z.string(),
  days_left: z.number().int().nullable(),
  resolved_at: z.string().nullable(),
  resolved_by: z.string(),
});
export type DataRequest = z.infer<typeof DataRequest>;

export const DataRequestList = z.object({ data_requests: z.array(DataRequest) });
export type DataRequestList = z.infer<typeof DataRequestList>;

/**
 * What a run answers: the closed task, and **either** the counts of what was
 * wiped (never a value) **or** the export document (kept loose: it is handed
 * to the guest as a file, and this app has no business knowing its shape).
 */
export const DataRequestRun = DataRequest.extend({
  anonymized: z.record(z.string(), z.number().int()).optional(),
  export: z.record(z.string(), z.unknown()).optional(),
});
export type DataRequestRun = z.infer<typeof DataRequestRun>;

/**
 * The refusal a stale tablet gets, and the only thing it needs to redraw.
 *
 * `assertOrderTransition` in `packages/domain` builds it: what the order
 * actually is, what was asked for, and **what is allowed instead**. The screen
 * shows the server's sentence verbatim and replaces its buttons with `allowed`,
 * so a kitchen that pressed Ready on an order somebody already cancelled learns
 * both facts at once.
 */
export const OrderTransitionDetail = z.object({
  reason: z.string(),
  status: OrderStatus.optional(),
  requested: OrderStatus.optional(),
  allowed: z.array(OrderStatus).optional(),
});
export type OrderTransitionDetail = z.infer<typeof OrderTransitionDetail>;

export const DepartureInstanceStatus = z.enum([
  'scheduled',
  'cancelled_weather',
  'cancelled',
  'completed',
]);
export type DepartureInstanceStatus = z.infer<typeof DepartureInstanceStatus>;

export const DepartureInstance = z.object({
  departure_instance_id: z.string(),
  service_id: z.string(),
  service_name: z.string(),
  resource_id: z.string(),
  vessel: z.string().nullable(),
  slot: z.object({ start: IsoDateTime, end: IsoDateTime }),
  seats_total: z.number(),
  seats_claimed: z.number(),
  seats_remaining: z.number(),
  bookings: z.number(),
  status: DepartureInstanceStatus,
  private: z.boolean(),
  cancelled_at: z.string().nullable(),
  cancellation_reason: z.string().nullable(),
});
export type DepartureInstance = z.infer<typeof DepartureInstance>;

export const DepartureList = z.object({ departures: z.array(DepartureInstance) });

export const ManifestEntry = z.object({
  booking_id: z.string(),
  guest_ref: z.string(),
  guest_name: z.string(),
  party_size: z.number(),
  /** Which lanes, not just how many — what a divemaster kits the boat from. */
  seats: z.array(z.number()),
  value_cents: z.number(),
  status: z.string(),
  source: z.string(),
  notes: z.string(),
  tickets: z.array(
    z.object({
      name: z.string(),
      seats: z.number(),
      unit_price_cents: z.number(),
    }),
  ),
  /**
   * **The waiver acknowledgement, or `null`** (owner's ruling 6, migration
   * 0049) — a ticked box with a timestamp and the version of the text, which
   * is what this is and is not an e-signature.
   *
   * `null` means *not asked*, not *refused*: a booking that should have
   * carried one never exists, because the server refuses the booking rather
   * than recording a refusal. So a captain reading `null` on a trip whose shop
   * publishes a waiver today is reading history, and the screen says "not
   * asked" rather than accusing the guest.
   *
   * `.nullish().catch(null)` so a manifest read from a deployment one release
   * behind still renders the boat.
   */
  waiver: z.object({ version: z.number(), accepted_at: z.string() }).nullish().catch(null),
  /**
   * **Whether the cascade texted this party** (verticals-plan V7 / D4), or
   * `null` when no call has touched the row. Three words the captain reads
   * per party after the 06:00 call: `texted` (queued through the outbox;
   * whether it left is `sms_deliveries`' to say), `no_consent` (a number on
   * file, no tick — a phone call the desk still owes), `no_phone` (a walk-in
   * paid in cash). `.catch(null)` for a server one release behind.
   */
  weather_notice: z.enum(['texted', 'no_consent', 'no_phone']).nullish().catch(null),
});
export type ManifestEntry = z.infer<typeof ManifestEntry>;

/**
 * One ticket type, priced — the base fare plus the tier's signed delta, summed
 * by the server. The sheet multiplies it by a seat count to show a total
 * **before the button**, and that total is never sent: `sellSeat` carries
 * `tier_id` and `seats`, and the store does the same sum from the catalog.
 */
export const TicketTierOffer = z.object({
  tier_id: z.string(),
  name: z.string(),
  unit_price_cents: z.number(),
  sold_out: z.boolean().catch(false),
});
export type TicketTierOffer = z.infer<typeof TicketTierOffer>;

export const DepartureDetail = DepartureInstance.extend({
  manifest: z.array(ManifestEntry),
  /**
   * What the desk sells from (V5), on the manifest because the sheet that
   * shows the manifest is the sheet that sells the next seat. `.catch([])`
   * so a manifest read from a `core-api` one release behind still renders the
   * boat — with no seat to sell, which is what that server can do.
   */
  tiers: z.array(TicketTierOffer).catch([]),
  /**
   * The document the desk reads to the guest before ticking on their word
   * (owner's ruling 6), whole, with the version the acknowledgement is anchored
   * to. `null` is "this tenant asks for none"; `.catch(null)` is the older
   * server above.
   */
  waiver: z
    .object({ title: z.string(), text: z.string(), version: z.number() })
    .nullish()
    .catch(null),
});
export type DepartureDetail = z.infer<typeof DepartureDetail>;

/**
 * What `POST /v1/departures/instances/{id}/bookings` answers: the manifest's
 * own entry for the party just sold, plus the hull's new count — the two things
 * the sheet repaints. `value_cents` is the catalog's; nothing this device sent
 * could have named it.
 */
export const SeatSold = ManifestEntry.extend({
  departure_instance_id: z.string(),
  seats_total: z.number(),
  seats_claimed: z.number(),
  seats_remaining: z.number(),
});
export type SeatSold = z.infer<typeof SeatSold>;

export const WeatherCalled = z.object({
  departure_instance_id: z.string(),
  service_id: z.string(),
  status: DepartureInstanceStatus,
  reason: z.string(),
  cancelled_at: z.string(),
  slot: z.object({ start: z.string(), end: z.string() }),
  seats_released: z.number(),
  /**
   * How many of the cancelled parties were texted and how many could not be
   * (V7 / D4) — the sentence under the manifest after the call. `.catch(0)`
   * so a server that predates the counts still reports the cascade.
   */
  guests_texted: z.number().catch(0),
  guests_not_texted: z.number().catch(0),
  bookings_cancelled: z.array(
    z.object({
      booking_id: z.string(),
      guest_ref: z.string().nullable(),
      party_size: z.number(),
      value_cents: z.number(),
    }),
  ),
  client_intent_id: z.string().optional(),
});
export type WeatherCalled = z.infer<typeof WeatherCalled>;

export const Stay = z.object({
  booking_id: z.string(),
  service_id: z.string(),
  room_type: z.string(),
  resource_id: z.string(),
  /** Which room of the type — what housekeeping is handed. */
  unit: z.number(),
  check_in: IsoDateTime,
  check_out: IsoDateTime,
  nights: z.number(),
  guests: z.number(),
  status: z.string(),
  source: z.string(),
  /** The folio: what the stay is worth, and what has already been taken. */
  value_cents: z.number(),
  prepaid_cents: z.number(),
  cancellation_fee_cents: z.number(),
  guest: z.object({ client_id: z.string(), display_name: z.string() }),
  notes: z.string(),
});
export type Stay = z.infer<typeof Stay>;

export const StayDay = z.object({
  date: IsoDate,
  arrivals: z.array(Stay),
  departures: z.array(Stay),
  in_house: z.array(Stay),
});
export type StayDay = z.infer<typeof StayDay>;

/**
 * The number before the button (V5): every room type the desk may sell, with
 * how many are free for the dates asked and what the stay would come to. The
 * total is the server's `quoteFor` — the same function `bookStay` then writes
 * into the folio — and it is **shown, never sent**: `bookStay` carries the room
 * and the dates, and the server prices them again.
 */
export const StayOffer = z.object({
  service_id: z.string(),
  name: z.string(),
  max_occupancy: z.number(),
  min_stay_nights: z.number(),
  /** Rooms of this type free for every night asked. `0` is "offer other dates". */
  rooms_available: z.number(),
  value_cents: z.number().nullable(),
  quote: z
    .object({
      nightly_cents: z.number(),
      fees_cents: z.number(),
      tax_cents: z.number(),
      total_cents: z.number(),
    })
    .nullable(),
});
export type StayOffer = z.infer<typeof StayOffer>;

export const StayOffers = z.object({
  check_in: IsoDate,
  check_out: IsoDate,
  nights: z.number(),
  party_size: z.number(),
  rooms: z.array(StayOffer),
});
export type StayOffers = z.infer<typeof StayOffers>;

/**
 * What `POST /v1/stays/{id}/cancel` answers with.
 *
 * `policy_fee_cents` beside `fee_cents` is §4.7's own distinction: what the
 * tiers said, and what the desk decided. The screen shows both, because "we let
 * this one go" and "the policy charged nothing" are different facts about a
 * Tuesday and the owner reads both in the same report.
 */
export const StayCancelled = z.object({
  booking_id: z.string(),
  status: z.string(),
  fee_cents: z.number(),
  policy_fee_cents: z.number(),
  waived: z.boolean(),
  days_out: z.number(),
  tier: z.string().nullable(),
  accommodation_cents: z.number(),
  policy_ref: z.string().nullable(),
  cancelled_at: z.string(),
  client_intent_id: z.string().optional(),
});
export type StayCancelled = z.infer<typeof StayCancelled>;

/**
 * **The calendars two systems share, as the desk reads them** (V3, migration
 * 0060): `GET /v1/stays/sync`.
 *
 * One read for two facts, because the screen paints them together and polls.
 *
 *  * **Which external calendars are stale.** `stale` is decided by the server
 *    from its own `ICAL_STALE_AFTER_HOURS`, not here: the front desk, the
 *    operator's CLI and any future alert must agree on the word, and a tablet
 *    that recomputed it from `last_pulled_at` would be a second definition of
 *    "stale" with a different clock behind it. `stale_after_hours` comes along
 *    so the screen can say *how* stale without deciding *whether*.
 *  * **Which OTA blocks lie over one of our confirmed stays.** Computed live
 *    from both tables, so the list is what is true now: cancel either side and
 *    the collision leaves without anyone resolving it.
 *
 * `guest_first_name` and nothing more about the guest. The desk has the stay's
 * whole record one tap away; a collision card's job is to say which room and
 * whose nights (§6.6's rule applied to a screen rather than to a wire).
 */
export const CalendarSync = z.object({
  service_id: z.string(),
  room_type: z.string(),
  unit: z.number(),
  ref: z.string(),
  label: z.string(),
  source: z.string(),
  active: z.boolean(),
  /** Whether the entry names a secret at all. One that does not is never pulled, so never stale. */
  configured: z.boolean(),
  /** The last pull that fetched, parsed and landed — never the last attempt. */
  last_pulled_at: z.string().nullable(),
  last_attempted_at: z.string().nullable(),
  /** One line, and never the URL, which is a credential. */
  last_error: z.string().nullable(),
  stale: z.boolean(),
});
export type CalendarSync = z.infer<typeof CalendarSync>;

export const ExternalCollision = z.object({
  booking_id: z.string(),
  guest_first_name: z.string(),
  room_type: z.string(),
  resource_id: z.string(),
  unit: z.number(),
  check_in: IsoDateTime,
  check_out: IsoDateTime,
  calendar_ref: z.string(),
  external_uid: z.string(),
  external_summary: z.string(),
  external_start: IsoDateTime,
  external_end: IsoDateTime,
  /** When the owner task was raised. `null` while the sweep has not seen it yet. */
  raised_at: z.string().nullable(),
});
export type ExternalCollision = z.infer<typeof ExternalCollision>;

export const StaySync = z.object({
  stale_after_hours: z.number(),
  calendars: z.array(CalendarSync),
  collisions: z.array(ExternalCollision),
});
export type StaySync = z.infer<typeof StaySync>;

/**
 * A pay link, in the **staff** projection (`routes/payments.ts`'s `staffView`).
 *
 * `status` is the agreement's four-value fold and `state` is the stored five:
 * the counter sees `approved` ("they clicked through, the money has not
 * landed") as distinct from `requested`, which is the difference between
 * chasing somebody and waiting a moment.
 */
export const PaymentLink = z.object({
  payment_link_id: z.string(),
  url: z.string(),
  amount_cents: z.number(),
  currency: z.string(),
  status: z.enum(['pending', 'paid', 'expired', 'cancelled']),
  referent: z.object({ kind: z.string(), id: z.string() }),
  expires_at: z.string(),
  state: z.enum(['requested', 'approved', 'completed', 'expired', 'failed']),
  source: z.string(),
  completed_at: z.string().nullable(),
  last_error: z.string().nullable(),
});
export type PaymentLink = z.infer<typeof PaymentLink>;

export const PaymentLinkList = z.object({ payment_links: z.array(PaymentLink) });

/**
 * The create response. `priced_by` is the till's own record of which regime
 * named the amount — `policy` or `staff` — and the panel labels the link with
 * it, because §6.5 permits the override and §7.4 rule 3's discipline is to
 * record what happened rather than reconstruct it.
 */
export const PaymentLinkCreated = PaymentLink.omit({
  state: true,
  source: true,
  completed_at: true,
  last_error: true,
}).extend({
  priced_by: z.enum(['policy', 'staff', 'order']),
  client_intent_id: z.string().optional(),
});
export type PaymentLinkCreated = z.infer<typeof PaymentLinkCreated>;

export { ErrorBody };

/** One frame of `GET /v1/events/stream`, after the SSE envelope is stripped. */
export const StreamEventData = z.object({
  event_id: z.string(),
  type: z.string(),
  subject_id: z.string().nullable(),
  occurred_at: z.string(),
  payload: z.unknown().optional(),
});
export type StreamEventData = z.infer<typeof StreamEventData>;

/* ------------------------------------------------------------------ */
/* Photos (docs/tour-photos-design.md decision 5)                      */
/* ------------------------------------------------------------------ */

/**
 * An album, as the Albums screen reads it.
 *
 * **No code and no hash**, mirroring `core-api`'s projection. The code crosses
 * this wire in exactly two responses — the create and the regenerate — and is
 * never readable back, which is the whole property `code_hash` exists for. What
 * the screen shows instead is `code_issued_at`: the answer to the question a
 * confused family actually produces, which is *since when has this card worked*.
 */
export const Album = z.object({
  album_id: z.string(),
  title: z.string(),
  departure_instance_id: z.string().nullable(),
  client_id: z.string().nullable(),
  status: z.enum(['active', 'revoked']),
  expires_at: z.string(),
  code_issued_at: z.string(),
  download_ttl_seconds: z.number(),
  photo_count: z.number(),
  created_at: z.string(),
});
export type Album = z.infer<typeof Album>;

export const AlbumList = z.object({ albums: z.array(Album) });
export type AlbumList = z.infer<typeof AlbumList>;

export const AlbumMedia = z.object({
  media_id: z.string(),
  position: z.number(),
  storage_path: z.string(),
  content_type: z.string(),
  size_bytes: z.number(),
  filename: z.string(),
  /** A short signed GET for the tile; absent where the deployment stores no photographs. */
  view: z.string().optional(),
});
export type AlbumMedia = z.infer<typeof AlbumMedia>;

export const AlbumDetail = Album.extend({ media: z.array(AlbumMedia) });
export type AlbumDetail = z.infer<typeof AlbumDetail>;

/**
 * The create response — **the one place a code exists on this wire**, and it is
 * shown once and stored nowhere.
 *
 * `media` is always `[]` here and is present only so the type matches the read.
 */
export const AlbumCreated = AlbumDetail.extend({ code: z.string() });
export type AlbumCreated = z.infer<typeof AlbumCreated>;

/** The other place. Regenerate = revoke + new: the old card is dead on commit. */
export const AlbumCodeIssued = z.object({
  album_id: z.string(),
  code: z.string(),
  code_issued_at: z.string(),
});
export type AlbumCodeIssued = z.infer<typeof AlbumCodeIssued>;

/**
 * The signed PUT URLs a browser uploads through (decision 4: "photo bytes never
 * transit our services").
 *
 * `media_id` is the server's, and it is what ties the upload to the row the
 * finalize step writes. The browser never invents one, and the path it goes to
 * is not on this wire at all — it is derived from the ids inside the signature.
 */
export const AlbumUploadUrls = z.object({
  album_id: z.string(),
  expires_in_seconds: z.number(),
  uploads: z.array(
    z.object({
      media_id: z.string(),
      content_type: z.string(),
      filename: z.string().optional(),
      url: z.string(),
    }),
  ),
});
export type AlbumUploadUrls = z.infer<typeof AlbumUploadUrls>;

/**
 * What landed and what did not.
 *
 * Partial success is the ordinary case on a dock, so the response is two lists
 * rather than a status: a photographer who lost one frame must not be told the
 * whole batch failed, and must be told *which* one to send again.
 */
export const AlbumFinalized = z.object({
  album_id: z.string(),
  recorded: z.array(AlbumMedia),
  refused: z.array(z.object({ media_id: z.string(), reason: z.string() })),
});
export type AlbumFinalized = z.infer<typeof AlbumFinalized>;

export const AlbumDeleted = z.object({ album_id: z.string(), deleted_photos: z.number() });
export type AlbumDeleted = z.infer<typeof AlbumDeleted>;

export const AlbumMediaDeleted = z.object({
  album_id: z.string(),
  media_id: z.string(),
  storage_path: z.string(),
});
export type AlbumMediaDeleted = z.infer<typeof AlbumMediaDeleted>;

/* ------------------------------------------------------------------ */
/* The concierge (../../aiconcierge/docs/partner-provisioning-api.md)  */
/* ------------------------------------------------------------------ */

/**
 * What `core-api` proxies back from aiconcierge.
 *
 * **Every field is nullish**, which is a deliberate departure from the rest of
 * this file. Everything else here is strict because a malformed row is written
 * into the mirror and repeated offline for as long as the tunnel lasts. Nothing
 * on this screen is mirrored — it is a `useRemote` read, online only, like the
 * kitchen pass and the dock — and the payload is a *partner's*, which changes
 * on their release schedule. A settings panel that failed to render because
 * aiconcierge added a field would be an outage caused by somebody else's
 * deploy.
 */
export const ConciergePartner = z.object({
  tenant_id: z.string(),
  status: z.string().nullish(),
  provider: z.object({ status: z.string().nullish(), detail: z.string().nullish() }).nullish(),
  catalog_source: z
    .object({ version: z.number().nullish(), synced_at: z.string().nullish() })
    .nullish(),
  profile_source: z
    .object({ version: z.number().nullish(), synced_at: z.string().nullish() })
    .nullish(),
  settings: z
    .object({
      autonomy: z.string().nullish(),
      autonomous_value_ceiling_cents: z.number().nullish(),
      greeting: z.string().nullish(),
      voice_greeting: z.string().nullish(),
      tone: z
        .object({
          voice: z.string().nullish(),
          formality: z.string().nullish(),
          avoid: z.array(z.string()).nullish(),
        })
        .nullish(),
      voice_mode: z.string().nullish(),
      owner_number: z.string().nullish(),
      guardrails: z.record(z.string(), z.unknown()).nullish(),
      services: z.array(z.object({ id: z.string(), bookable_by_ai: z.boolean() })).nullish(),
    })
    .nullish(),
  config_version: z.number().nullish(),
  site_key: z.string().nullish(),
  console_url: z.string().nullish(),
  missing: z.array(z.string()).nullish(),
});
export type ConciergePartner = z.infer<typeof ConciergePartner>;

/** One shape for all four verbs, so the panel parses one schema. */
export const ConciergeState = z.object({
  tenant_id: z.string(),
  role: z.string(),
  /** False means this deployment has no agent connected. One sentence, no button. */
  configured: z.boolean(),
  concierge: ConciergePartner.nullable(),
  unreachable: z.string().nullable().catch(null).default(null),
  site_key: z.string().nullable().catch(null).default(null),
  console_url: z.string().nullable().catch(null).default(null),
  note: z.string(),
});
export type ConciergeState = z.infer<typeof ConciergeState>;

/* ------------------------------------------------------------------ */
/* Requests — GET /v1/inquiries (docs/lovespa-discovery.md §2.3)       */
/* ------------------------------------------------------------------ */

/**
 * One request from the guest site, as `core-api` projects the
 * `inquiry.received` event that carries it.
 *
 * **Everything is read tolerantly and every string may be empty**, because the
 * payload is JSONB in the outbox rather than a row in a table with columns. A
 * row written by the ordinary contact form has no treatment on it; a row
 * written before this wave has none of the three new keys at all, and
 * `core-api`'s `serialize` answers `''` for each — so the screen has one shape
 * to draw rather than two.
 *
 * **`verified` is the field that matters on the screen.** The name and the
 * telephone number were *typed into a public form*; nobody proved them. A
 * member of staff about to ring somebody back is entitled to know that, which
 * is why it rides all the way here rather than being dropped as plumbing.
 *
 * **`answered_at` is the one state it has** (X1), and it is the server's: the
 * desk's "Mark answered" writes an `inquiry.answered` event beside the inquiry
 * and every device reads the same instant back. A local tick would have been a
 * queue whose state lives on one iPad — ticked on the tablet, untouched on her
 * phone — which is why this field did not exist until there was somewhere on
 * the server to put it. `null` while nobody has answered; tolerant, for the
 * same reason as every other field here.
 */
export const Inquiry = z.object({
  inquiry_id: z.string(),
  received_at: z.string(),
  name: z.string().catch('').default(''),
  contact: z.string().catch('').default(''),
  occasion: z.string().catch('').default(''),
  preferred_date: z.string().catch('').default(''),
  message: z.string().catch('').default(''),
  /** The treatment asked about, when the site takes requests. `''` otherwise. */
  item_id: z.string().catch('').default(''),
  /** Its name **as the catalog had it when the request was made** — a snapshot. */
  item_name: z.string().catch('').default(''),
  /** "Weekday afternoons" — the guest's own words, never parsed into a date. */
  preferred: z.string().catch('').default(''),
  source: z.string().catch('site').default('site'),
  verified: z.boolean().catch(false).default(false),
  answered_at: z.string().nullable().catch(null).default(null),
});
export type Inquiry = z.infer<typeof Inquiry>;

/** `POST /v1/inquiries/{id}/answered` — the instant, and whether it was already so. */
export const InquiryAnswered = z.object({
  inquiry_id: z.string(),
  answered_at: z.string(),
  already_answered: z.boolean().catch(false).default(false),
});
export type InquiryAnswered = z.infer<typeof InquiryAnswered>;

export const InquiryList = z.object({
  inquiries: z.array(Inquiry),
  window_days: z.number().catch(30).default(30),
  complete: z.boolean().catch(true).default(true),
});

/* ------------------------------------------------------------------ */
/* Pickup reservations (docs/lovespa-gaps-plan.md C1, migration 0039)  */
/* ------------------------------------------------------------------ */

/**
 * A reservation's five states. A **separate enum from `OrderStatus`**, because
 * they are separate tables with separate lifecycles — the migration spends
 * three paragraphs on why, and the short version is that `order_status` is a
 * wire enum the provider agreement names and this one needs `expired`.
 */
export const ReservationStatus = z.enum(['placed', 'ready', 'collected', 'cancelled', 'expired']);
export type ReservationStatus = z.infer<typeof ReservationStatus>;

export const ReservationPayment = z.enum(['at_pickup', 'prepay']);
export type ReservationPayment = z.infer<typeof ReservationPayment>;

export const ReservationLine = z.object({
  line_id: z.string(),
  item_id: z.string().nullable(),
  name: z.string(),
  qty: z.number(),
  unit_price_cents: z.number(),
  line_total_cents: z.number(),
});

export const Reservation = z.object({
  reservation_id: z.string(),
  client_id: z.string(),
  client_name: z.string().nullable(),
  status: ReservationStatus,
  payment: ReservationPayment.catch('at_pickup').default('at_pickup'),
  /** What the server says may follow. The screen holds no second table. */
  next_statuses: z.array(z.string()).catch([]).default([]),
  subtotal_cents: z.number(),
  total_cents: z.number(),
  prepaid_cents: z.number().catch(0).default(0),
  outstanding_cents: z.number(),
  notes: z.string().catch('').default(''),
  placed_at: z.string(),
  ready_at: z.string().nullable().catch(null).default(null),
  collected_at: z.string().nullable().catch(null).default(null),
  expires_at: z.string().nullable().catch(null).default(null),
  lines: z.array(ReservationLine),
});
export type Reservation = z.infer<typeof Reservation>;

export const ReservationList = z.object({ reservations: z.array(Reservation) });

/**
 * **"Has this shop ever had a reservation?"** — the shell's probe
 * (`Mirror.refreshTenantFacts`), `GET /v1/reservations?limit=1`.
 *
 * Deliberately not `ReservationList`: the probe asks one yes-or-no question,
 * and a row that fails the full schema because a field moved is still a row.
 * A tab that vanished because a projection gained a field would be the shape
 * of the answer deciding the answer.
 */
export const ReservationPresence = z.object({ reservations: z.array(z.unknown()) });
export type ReservationList = z.infer<typeof ReservationList>;

/** The till's draft — what Checkout opens with (`GET /v1/reservations/{id}/lines`). */
export const ReservationForTill = z.object({
  reservation_id: z.string(),
  client_id: z.string(),
  client_name: z.string().nullable(),
  status: ReservationStatus,
  payment: ReservationPayment,
  outstanding_cents: z.number(),
  lines: z.array(
    z.object({
      item_id: z.string().nullable(),
      name: z.string(),
      qty: z.number(),
      unit_price_cents: z.number(),
    }),
  ),
});
export type ReservationForTill = z.infer<typeof ReservationForTill>;

/* ---- the newsletter (E3, module `clients`) ------------------------ */

/**
 * The six block kinds, mirrored from `@yourstore/email`'s `NewsletterBlock`.
 *
 * Mirrored rather than imported, and it is the same trade every schema in this
 * file makes: `apps/app` parses what the server sent rather than sharing the
 * server's types, so a field the API stops sending is a parse failure here
 * instead of a `undefined` two components deep. The authority is the package;
 * this is the wire's picture of it.
 *
 * A `product` block carries **an item id and no price** — B7 — so the composer
 * shows the price by looking the item up in the mirrored product list, and the
 * email shows the price the server resolved at send time. If those two ever
 * disagree, the email is right.
 */
export const NewsletterBlock = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('heading'),
    text: z.string(),
    level: z
      .union([z.literal(1), z.literal(2)])
      .catch(2)
      .default(2),
  }),
  z.object({ kind: z.literal('text'), text: z.string() }),
  z.object({
    kind: z.literal('image'),
    url: z.string(),
    alt: z.string().catch(''),
    link: z.string().optional(),
  }),
  z.object({
    kind: z.literal('product'),
    item_id: z.string(),
    blurb: z.string().optional(),
  }),
  z.object({ kind: z.literal('button'), label: z.string(), url: z.string() }),
  z.object({ kind: z.literal('divider') }),
]);
export type NewsletterBlock = z.infer<typeof NewsletterBlock>;

export const NewsletterStats = z.object({
  queued: z.number().catch(0),
  sent: z.number().catch(0),
  failed: z.number().catch(0),
  bounced: z.number().catch(0),
  complained: z.number().catch(0),
  suppressed: z.number().catch(0),
  unsubscribed: z.number().catch(0),
});
export type NewsletterStats = z.infer<typeof NewsletterStats>;

export const NewsletterIssue = z.object({
  issue_id: z.string(),
  subject: z.string(),
  preheader: z.string().catch(''),
  blocks: z.array(NewsletterBlock).catch([]),
  status: z.enum(['draft', 'scheduled', 'sending', 'sent', 'cancelled']),
  scheduled_for: z.string().nullable(),
  sent_at: z.string().nullable(),
  created_by: z.string().catch(''),
  stats: NewsletterStats,
  created_at: z.string(),
  updated_at: z.string(),
});
export type NewsletterIssue = z.infer<typeof NewsletterIssue>;

export const NewsletterAudience = z.object({
  total: z.number(),
  truncated: z.boolean().catch(false),
});

export const NewsletterIssueList = z.object({
  issues: z.array(NewsletterIssue),
  audience: NewsletterAudience,
});
export type NewsletterIssueList = z.infer<typeof NewsletterIssueList>;

/**
 * `GET /v1/newsletter/sender` — what the business's email looks like, and every
 * reason it could not be sent.
 *
 * `blockers` is a list of **sentences written for the owner**, not codes: the
 * screen prints them as they arrive. That is the same rule the rest of this app
 * follows for `ApiFailure.message` (`messages/en.ts`'s own boundary note) —
 * server sentences are printed verbatim and never keyed, because the server is
 * the only thing that knows which of eight reasons applies.
 */
export const NewsletterSender = z.object({
  configured: z.boolean(),
  from: z.string().catch(''),
  reply_to: z.string().nullable(),
  postal_address: z.string().catch(''),
  list_id: z.string().nullable(),
  audience: NewsletterAudience,
  blockers: z.array(z.string()).catch([]),
  join_url: z.string().nullable(),
});
export type NewsletterSender = z.infer<typeof NewsletterSender>;

/**
 * The one field of `NewsletterSender` the shell reads to decide whether the
 * Newsletter tab is drawn (`Mirror.refreshTenantFacts`) — `ReservationPresence`'s
 * reason: the tab must not disappear because a field it never shows moved.
 */
export const NewsletterSenderConfigured = z.object({ configured: z.boolean() });

export const NewsletterPreview = z.object({
  issue_id: z.string(),
  locale: z.string(),
  html: z.string(),
  text: z.string(),
  subject: z.string(),
  from: z.string().catch(''),
  reply_to: z.string().nullable(),
  postal_address: z.string().catch(''),
  audience: NewsletterAudience,
  blockers: z.array(z.string()).catch([]),
  warnings: z.array(z.string()).catch([]),
});
export type NewsletterPreview = z.infer<typeof NewsletterPreview>;

export const NewsletterSendResult = z.object({
  test: z.boolean(),
  queued: z.number(),
  audience: z.number(),
});
